<template>
  <div class="bzc-sum" role="dialog" aria-modal="true" aria-label="One-page client summary" data-test="ask-bizminer-summary-modal" @click.self="$emit('close')">
    <div class="bzc-sum-bar">
      <div class="bzc-sum-bar-text">
        <div class="bzc-sum-bar-title">One-page client summary</div>
        <div class="bzc-sum-bar-sub">Preview &middot; built from your report. To save a copy, choose Save as PDF in the print window.</div>
      </div>
      <button v-if="view" ref="print" type="button" class="bzc-sum-btn is-primary" data-test="ask-bizminer-summary-print" @click="print">
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"></path></svg>
        Print
      </button>
      <button ref="close" type="button" class="bzc-sum-btn" data-test="ask-bizminer-summary-close" @click="$emit('close')">Close</button>
    </div>

    <div class="bzc-sum-scroll">
      <div v-if="state.phase === 'loading'" class="bzc-sum-note" role="status" data-test="ask-bizminer-summary-loading">
        <svg class="bzc-spin" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
        Laying out your summary&hellip;
      </div>

      <div v-else-if="state.phase === 'error'" class="bzc-sum-note is-error" role="alert" data-test="ask-bizminer-summary-error">
        <span>{{ state.error }}</span>
        <button v-if="state.retryable" type="button" class="bzc-link-btn" @click="$emit('retry')">Try again</button>
      </div>

      <!-- Everything below is drawn as text (interpolation only), never as HTML. -->
      <article v-else-if="view" class="bzc-sum-sheet" data-test="ask-bizminer-summary-sheet">
        <header class="bzc-sum-band">
          <span class="bzc-sum-brand">
            <img src="/img/logo-symbol-blue.png" alt="" />
            <b>Bizminer</b>
          </span>
          <span class="bzc-sum-band-right">
            <span class="bzc-sum-doc">{{ view.title }}</span>
            <span v-if="view.preparedOn" class="bzc-sum-date">{{ view.preparedOn }}</span>
          </span>
        </header>
        <div class="bzc-sum-rule"></div>

        <div class="bzc-sum-main">
          <h1 class="bzc-sum-name" data-test="ask-bizminer-summary-name">{{ view.borrower }}</h1>
          <p v-if="view.subtitle" class="bzc-sum-slice">{{ view.subtitle }}</p>

          <div v-if="view.tiles.length" class="bzc-sum-tiles">
            <div v-for="t in view.tiles" :key="t.label" class="bzc-sum-tile">
              <div class="bzc-sum-tile-label">{{ t.label }}</div>
              <div class="bzc-sum-tile-value">{{ t.value }}</div>
              <div v-if="t.note" class="bzc-sum-tile-note">{{ t.note }}</div>
            </div>
          </div>

          <section v-if="view.variances.length" class="bzc-sum-sec">
            <h2>How the business compares with its industry</h2>
            <table class="bzc-sum-table">
              <thead>
                <tr><th>Measure</th><th>The business</th><th>Industry</th><th>Variance</th></tr>
              </thead>
              <tbody>
                <tr v-for="v in view.variances" :key="v.title">
                  <th scope="row">{{ v.title }}</th>
                  <td>{{ v.yours }}</td>
                  <td>{{ v.industry }}</td>
                  <td>{{ v.variance }}<span v-if="v.rating" class="bzc-sum-rate"><i :class="'is-' + v.tone"></i>{{ v.rating }}</span></td>
                </tr>
              </tbody>
            </table>
          </section>

          <section v-if="view.market" class="bzc-sum-sec" data-test="ask-bizminer-summary-market">
            <h2>Position in the market<template v-if="view.market.period"> &middot; {{ view.market.period }}</template></h2>
            <table class="bzc-sum-table">
              <tbody>
                <tr v-for="f in view.market.figures" :key="f.label"><th scope="row">{{ f.label }}</th><td>{{ f.value }}</td></tr>
              </tbody>
            </table>
          </section>

          <section v-if="view.values.length" class="bzc-sum-sec">
            <h2>Estimated value</h2>
            <table class="bzc-sum-table">
              <tbody>
                <tr v-for="v in view.values" :key="v.label"><th scope="row">{{ v.label }}</th><td>{{ v.value }}</td></tr>
              </tbody>
            </table>
          </section>

          <section v-if="view.multiples.length" class="bzc-sum-sec">
            <h2>Industry multiples</h2>
            <table class="bzc-sum-table">
              <thead><tr><th>Approach</th><th>Low</th><th>Median</th><th>High</th></tr></thead>
              <tbody>
                <tr v-for="m in view.multiples" :key="m.label"><th scope="row">{{ m.label }}</th><td>{{ m.low }}</td><td>{{ m.median }}</td><td>{{ m.high }}</td></tr>
              </tbody>
            </table>
          </section>

          <section v-if="view.revenue.length > 1" class="bzc-sum-sec">
            <h2>{{ view.revenueTitle }}</h2>
            <p class="bzc-sum-rev"><span v-for="r in view.revenue" :key="r.label"><b>{{ r.label }}</b> {{ r.value }}</span></p>
          </section>

          <section v-if="view.points.length" class="bzc-sum-sec">
            <h2>Points to discuss</h2>
            <ol class="bzc-sum-points" data-test="ask-bizminer-summary-points">
              <li v-for="(p, i) in view.points" :key="i"><span class="bzc-sum-num">{{ i + 1 }}</span><span>{{ p }}</span></li>
            </ol>
          </section>
        </div>

        <footer class="bzc-sum-foot">
          <p data-test="ask-bizminer-summary-disclaimer">{{ view.disclaimer }}</p>
          <p class="bzc-sum-foot-line"><span>Source: Bizminer {{ product }}<template v-if="view.subtitle"> &middot; {{ view.subtitle }}</template></span><b>bizminer.com</b></p>
        </footer>
      </article>
    </div>
  </div>
</template>

<script>
import { summaryView } from './chatCore';

// The one-page client summary (CHAT-11, Lender demo): a preview with a Print button.
// The page is the chat service's own summary, laid out by chatCore.summaryView; this
// component moves itself to <body> so the print stylesheet can show only the sheet.
export default {
  name: 'client-summary',
  props: {
    state: { type: Object, required: true }, // {phase: loading|ready|error, data, error, retryable}
  },
  computed: {
    view() {
      return this.state.phase === 'ready' && this.state.data ? summaryView(this.state.data) : null;
    },
    product() {
      const names = { valuation_report: 'Valuation Report', market_report: 'Market Report' };
      return (this.view && names[this.view.kind]) || 'Financial Report';
    },
  },
  mounted() {
    document.body.appendChild(this.$el);
    document.body.classList.add('bzc-sum-open');
    document.addEventListener('keydown', this.onKey);
    this.$nextTick(() => (this.$refs.print || this.$refs.close || {}).focus && (this.$refs.print || this.$refs.close).focus());
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey);
    document.body.classList.remove('bzc-sum-open');
  },
  methods: {
    onKey(e) {
      if (e.key === 'Escape') this.$emit('close');
    },
    print() {
      window.print();
    },
  },
};
</script>

<style lang="scss">
$bzc-navy: #003666;
$bzc-cyan: #0cbcf2;

.bzc-sum {
  position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2000; display: flex; flex-direction: column; background: rgba(8, 24, 46, 0.72);
  font-family: Roboto, Arial, sans-serif; color: #22303f; text-align: left;
  p { margin: 0; }
}
body.bzc-sum-open { overflow: hidden; }
.bzc-sum-bar {
  flex: 0 0 auto; display: flex; align-items: center; gap: 10px; padding: 10px 16px; background: #0b2a4a; color: #fff;
}
.bzc-sum-bar-text { flex: 1 1 auto; min-width: 0; }
.bzc-sum-bar-title { font-family: Inter, Roboto, sans-serif; font-size: 16px; font-weight: 600; }
.bzc-sum-bar-sub { font-size: 12px; opacity: 0.8; }
.bzc-sum-btn {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 6px; min-height: 40px; padding: 0 16px; border-radius: 6px; border: 1px solid rgba(255, 255, 255, 0.55);
  background: transparent; color: #fff; font: inherit; font-size: 14px; font-weight: 500; cursor: pointer;
  &.is-primary { background: #fff; border-color: #fff; color: $bzc-navy; }
  &:focus-visible { outline: 3px solid $bzc-cyan; outline-offset: 2px; }
}
.bzc-sum-scroll { flex: 1 1 auto; min-height: 0; overflow: auto; padding: 22px 12px 40px; -webkit-overflow-scrolling: touch; }
.bzc-sum-note { max-width: 520px; margin: 40px auto; display: flex; align-items: center; justify-content: center; gap: 10px; padding: 16px; background: #fff; border-radius: 10px; font-size: 14px; &.is-error { color: #a3261c; flex-wrap: wrap; } }

// The page: US Letter proportions, white, one page.
.bzc-sum-sheet {
  width: 100%; max-width: 760px; margin: 0 auto; background: #fff; box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35); font-size: 13px; line-height: 1.45;
}
.bzc-sum-band { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 18px 28px; background: $bzc-navy; color: #fff; }
.bzc-sum-brand { display: inline-flex; align-items: center; gap: 8px; font-family: Inter, Roboto, sans-serif; font-size: 20px; img { height: 26px; width: auto; filter: brightness(0) invert(1); } b { font-weight: 600; } }
.bzc-sum-band-right { display: flex; flex-direction: column; align-items: flex-end; text-align: right; }
.bzc-sum-doc { font-family: Inter, Roboto, sans-serif; font-size: 15px; font-weight: 700; }
.bzc-sum-date { font-size: 11.5px; opacity: 0.85; }
.bzc-sum-rule { height: 4px; background: $bzc-cyan; }
.bzc-sum-main { padding: 20px 28px 8px; }
.bzc-sum-name { margin: 0; font-family: Inter, Roboto, sans-serif; font-size: 26px; font-weight: 700; line-height: 1.2; color: $bzc-navy; overflow-wrap: anywhere; }
.bzc-sum-slice { margin-top: 4px !important; font-size: 12.5px; color: #4f5a6b; }
.bzc-sum-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin: 16px 0 4px; }
.bzc-sum-tile { border: 1px solid #d7e3ee; border-radius: 6px; background: #f3f8fc; padding: 10px 12px; }
.bzc-sum-tile-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #5b6575; }
.bzc-sum-tile-value { font-family: Inter, Roboto, sans-serif; font-size: 24px; font-weight: 700; color: $bzc-navy; }
.bzc-sum-tile-note { font-size: 12px; color: #3c4656; }
.bzc-sum-sec { margin-top: 16px; }
.bzc-sum-sec h2 { margin: 0 0 6px; font-family: Inter, Roboto, sans-serif; font-size: 11.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: $bzc-navy; }
.bzc-sum-table {
  width: 100%; border-collapse: collapse; font-size: 12.5px;
  th, td { padding: 6px 6px; text-align: right; border-bottom: 1px solid #e3e9ef; font-weight: 400; }
  thead th { font-weight: 700; color: #3c4656; border-bottom: 2px solid $bzc-navy; }
  tbody th { text-align: left; font-weight: 500; color: #13213c; }
  th:first-child { text-align: left; }
}
.bzc-sum-rate { margin-left: 8px; font-size: 11.5px; color: #5b6575; i { display: inline-block; width: 8px; height: 8px; border-radius: 4px; margin-right: 4px; background: #8a95a5; &.is-good { background: #2d6731; } &.is-bad { background: #c4382c; } &.is-warn { background: #e0a100; } } }
.bzc-sum-rev span { margin-right: 16px; b { font-weight: 700; color: $bzc-navy; margin-right: 4px; } }
.bzc-sum-points { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; li { display: flex; gap: 10px; align-items: flex-start; } }
.bzc-sum-num { flex: 0 0 22px; width: 22px; height: 22px; border-radius: 11px; background: $bzc-navy; color: #fff; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
.bzc-sum-foot { margin: 18px 28px 0; padding: 12px 0 18px; border-top: 1px solid #d7dde5; font-size: 11px; color: #5b6575; }
.bzc-sum-foot-line { display: flex; justify-content: space-between; gap: 12px; margin-top: 4px !important; b { color: $bzc-navy; white-space: nowrap; } }

@media (max-width: 600px) {
  .bzc-sum-bar { flex-wrap: wrap; padding: 10px 12px; }
  .bzc-sum-btn { min-height: 44px; }
  .bzc-sum-scroll { padding: 12px 6px 30px; }
  .bzc-sum-band { padding: 14px 16px; }
  .bzc-sum-main { padding: 16px 16px 6px; }
  .bzc-sum-name { font-size: 21px; }
  .bzc-sum-foot { margin: 14px 16px 0; }
  .bzc-sum-table { font-size: 12px; th, td { padding: 6px 3px; } }
  .bzc-sum-rate { display: block; margin-left: 0; }
}

// Print: only the sheet, on one Letter page, with the navy band and tones kept.
@media print {
  @page { size: letter portrait; margin: 0.4in; }
  body.bzc-sum-open { overflow: visible !important; }
  body.bzc-sum-open > *:not(.bzc-sum) { display: none !important; }
  .bzc-sum { position: static !important; display: block !important; background: #fff !important; }
  .bzc-sum-bar { display: none !important; }
  .bzc-sum-scroll { overflow: visible !important; padding: 0 !important; }
  .bzc-sum-sheet { max-width: none !important; box-shadow: none !important; font-size: 12px; }
  .bzc-sum-sec, .bzc-sum-tiles, .bzc-sum-points li { break-inside: avoid; }
  .bzc-sum-sheet, .bzc-sum-sheet * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
</style>
